import { FC } from 'react';
import { Button, Checkbox, Input, Radio, Select } from 'antd';
import {
  ClockCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import {
  CLIENT_TYPE_TABS,
  ClientType,
  FUNDING_SOURCES,
} from '../CourseEnrollment.const';
import { ClientForm, FormUpdater } from '../CourseEnrollment.types';
import css from '../CourseEnrollment.module.css';

interface Props {
  clientType: ClientType;
  onClientTypeChange: (t: ClientType) => void;
  allowedClientTypes: ClientType[];
  form: ClientForm;
  updateForm: FormUpdater<ClientForm>;
  prevRequisitesId: string | undefined;
  onPrevRequisitesChange: (v: string | undefined) => void;
  onImnsLookup: () => void;
}

const ClientDataStep: FC<Props> = ({
  clientType,
  onClientTypeChange,
  allowedClientTypes,
  form,
  updateForm,
  prevRequisitesId,
  onPrevRequisitesChange,
  onImnsLookup,
}) => {
  const visibleTabs = CLIENT_TYPE_TABS.filter((t) => allowedClientTypes.includes(t.value));
  return (
  <>
    <div className={css.Tabs}>
      {visibleTabs.map((t) => (
        <button
          key={t.value}
          type='button'
          className={`${css.Tab} ${
            clientType === t.value ? css.TabActive : ''
          }`}
          onClick={() => onClientTypeChange(t.value)}
        >
          {t.label}
        </button>
      ))}
    </div>

    <div className={css.SectionCard}>
      <div className={css.PrevReq}>
        <span className={css.PrevReqLabel}>
          <span className={css.PrevReqIcon}>
            <ClockCircleOutlined />
          </span>
          Использовать ранее введенные реквизиты
        </span>
        <Select
          className={css.PrevReqSelect}
          placeholder='Выберите вариант'
          allowClear
          disabled
          value={prevRequisitesId}
          onChange={(v) => onPrevRequisitesChange(v)}
          options={[]}
        />
      </div>
    </div>

    <div className={css.SectionCard}>
      {clientType === 'legal' && (
        <>
          <div className={css.UnpBlock}>
            <div className={css.Field}>
              <span className={css.FieldLabel}>УНП *</span>
              <div className={css.UnpRow}>
                <Input
                  className={css.UnpInput}
                  placeholder='Введите УНП'
                  value={form.unp}
                  onChange={(e) => updateForm('unp', e.target.value)}
                />
                <Button className={css.UnpBtn} icon={<ReloadOutlined />} onClick={onImnsLookup}>
                  Получить данные
                </Button>
              </div>
            </div>
            <div className={css.Notice}>
              <span className={css.NoticeIcon}>
                <InfoCircleOutlined />
              </span>
              <span>
                Если API ИМНС временно недоступен, наименование организации и
                юридический адрес можно ввести вручную.
              </span>
            </div>
          </div>

          <div className={css.FormGrid}>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Наименование организации *</span>
              <Input
                placeholder='Введите наименование организации'
                value={form.organization}
                onChange={(e) => updateForm('organization', e.target.value)}
              />
            </div>

            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Юридический адрес *</span>
              <Input
                placeholder='Введите юридический адрес'
                value={form.legalAddress}
                onChange={(e) => updateForm('legalAddress', e.target.value)}
              />
            </div>

            <div className={`${css.AddrBlock} ${css.FieldFull}`}>
              <Checkbox
                className={css.AddrCheck}
                checked={form.postalDiffers}
                onChange={(e) => updateForm('postalDiffers', e.target.checked)}
              >
                Почтовый адрес отличается от юридического
              </Checkbox>
              {form.postalDiffers && (
                <Input
                  placeholder='Введите почтовый адрес'
                  value={form.postalAddress}
                  onChange={(e) =>
                    updateForm('postalAddress', e.target.value)
                  }
                />
              )}
            </div>

            <div className={css.Field}>
              <span className={css.FieldLabel}>ФИО подписанта *</span>
              <Input
                placeholder='Фамилия Имя Отчество'
                value={form.signerName}
                onChange={(e) => updateForm('signerName', e.target.value)}
              />
            </div>
            <div className={css.Field}>
              <span className={css.FieldLabel}>Должность подписанта *</span>
              <Input
                placeholder='Введите должность подписанта'
                value={form.signerPosition}
                onChange={(e) =>
                  updateForm('signerPosition', e.target.value)
                }
              />
            </div>

            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Основание подписания *</span>
              <Input
                placeholder='Например: Устава, Доверенность № ... от ...'
                value={form.signingBasis}
                onChange={(e) => updateForm('signingBasis', e.target.value)}
              />
              <span className={css.FieldHint}>
                Укажите документ, на основании которого действующий подписант
                имеет право подписывать договор.
              </span>
            </div>
          </div>

          <div style={{ height: 16 }} />
        </>
      )}

      {clientType === 'ip' && (
        <>
          <div className={css.UnpBlock}>
            <div className={css.Field}>
              <span className={css.FieldLabel}>УНП *</span>
              <div className={css.UnpRow}>
                <Input
                  className={css.UnpInput}
                  placeholder='Введите УНП'
                  value={form.unp}
                  onChange={(e) => updateForm('unp', e.target.value)}
                />
                <Button className={css.UnpBtn} icon={<ReloadOutlined />} onClick={onImnsLookup}>
                  Получить данные
                </Button>
              </div>
            </div>
            <div className={css.Notice}>
              <span className={css.NoticeIcon}>
                <InfoCircleOutlined />
              </span>
              <span>
                Если API ИМНС временно недоступен, наименование ИП можно
                ввести вручную.
              </span>
            </div>
          </div>

          <div className={css.FormGrid}>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Наименование ИП *</span>
              <Input
                placeholder='Индивидуальный предприниматель ...'
                value={form.organization}
                onChange={(e) => updateForm('organization', e.target.value)}
              />
            </div>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Почтовый адрес *</span>
              <Input
                placeholder='Введите почтовый адрес'
                value={form.postalAddress}
                onChange={(e) => updateForm('postalAddress', e.target.value)}
              />
            </div>
          </div>

          <div style={{ height: 16 }} />
        </>
      )}

      {clientType === 'individual' && (
        <>
          <div className={css.FormGrid}>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>ФИО *</span>
              <Input
                placeholder='Фамилия Имя Отчество'
                value={form.individualFullName}
                onChange={(e) =>
                  updateForm('individualFullName', e.target.value)
                }
              />
            </div>
            <div className={css.Field}>
              <span className={css.FieldLabel}>Номер паспорта *</span>
              <Input
                placeholder='MP1234567'
                value={form.passportNumber}
                onChange={(e) =>
                  updateForm('passportNumber', e.target.value)
                }
              />
            </div>
            <div className={css.Field}>
              <span className={css.FieldLabel}>Дата выдачи паспорта *</span>
              <Input
                placeholder='ДД.ММ.ГГГГ'
                value={form.passportIssueDate}
                onChange={(e) =>
                  updateForm('passportIssueDate', e.target.value)
                }
              />
            </div>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Кем выдан паспорт *</span>
              <Input
                placeholder='УВД ...'
                value={form.passportIssuer}
                onChange={(e) =>
                  updateForm('passportIssuer', e.target.value)
                }
              />
            </div>
            <div className={`${css.Field} ${css.FieldFull}`}>
              <span className={css.FieldLabel}>Почтовый адрес *</span>
              <Input
                placeholder='Введите почтовый адрес'
                value={form.postalAddress}
                onChange={(e) => updateForm('postalAddress', e.target.value)}
              />
            </div>
          </div>

          <div style={{ height: 16 }} />
        </>
      )}

      <div className={css.FormGrid}>
        <div className={css.Field}>
          <span className={css.FieldLabel}>Телефон *</span>
          <Input
            placeholder='Введите телефон'
            value={form.phone}
            onChange={(e) => updateForm('phone', e.target.value)}
          />
        </div>
        <div className={css.Field}>
          <span className={css.FieldLabel}>E-mail *</span>
          <Input
            placeholder='Введите e-mail'
            value={form.email}
            onChange={(e) => updateForm('email', e.target.value)}
          />
        </div>

        <div className={`${css.Field} ${css.FieldFull}`}>
          <span className={css.FieldLabel}>Источник финансирования *</span>
          <div className={css.FundingRow}>
            <Radio.Group
              value={form.funding}
              onChange={(e) => updateForm('funding', e.target.value)}
            >
              {FUNDING_SOURCES.map((f) => (
                <Radio key={f.value} value={f.value}>
                  {f.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export default ClientDataStep;
